<template>
	<a-modal
		class="g-pay-file-unlock-edit-modal"
		:visible="visible"
		title="文件付费编辑"
		width="730px"
		:centered="true"
		:mask-closable="false"
		:destroy-on-close="true"
		@cancel="onCancel"
	>
		<div class="g-edit-modal__scroll">
		<a-form layout="vertical" class="g-pay-file-unlock-edit-form">
			<div class="g-pay-file-unlock-edit-form__row">
				<a-form-item label="现价" class="g-pay-file-unlock-edit-form__col">
					<a-input v-model:value="form.price" placeholder="例如 9.90" />
				</a-form-item>
				<a-form-item label="免费预览页数" class="g-pay-file-unlock-edit-form__col">
					<a-input-number
						v-model:value="form.freePreviewCount"
						:min="0"
						:max="pageCount || undefined"
						:precision="0"
						style="width: 100%;"
					/>
				</a-form-item>
			</div>
			<div class="g-pay-file-unlock-edit-form__row">
				<a-form-item label="按钮文字" class="g-pay-file-unlock-edit-form__col">
					<a-input v-model:value="form.buttonText" placeholder="解锁全部内容" />
				</a-form-item>
				<a-form-item label="按钮颜色" class="g-pay-file-unlock-edit-form__col">
					<div class="g-pay-file-unlock-edit-form__colors">
						<div class="g-pay-file-unlock-edit-form__color-item">
							<ColorPicker v-model="form.buttonBg" :swatch-width="29" :swatch-height="29" />
							<a-input v-model:value="form.buttonBg"  />
						</div>
					</div>
				</a-form-item>
			</div>
		</a-form>

		<div class="g-pay-file-unlock-edit">
			<div class="g-pay-file-unlock-edit__doc" @click.stop>
				<div v-if="isParsing" class="g-pay-file-unlock-edit__processing">
					<div class="g-pay-file-unlock-edit__processing-line">
						<a-spin size="small" />
						<span class="g-pay-file-unlock-edit__processing-text">文档转换中，请稍后...</span>
					</div>
					<UploadProgressPanel
						:tasks="uploadTasks"
						variant="row"
						hide-filename
						status-text=""
					/>
				</div>
				<template v-else>
					<div class="g-pay-file-unlock-edit__doc-info">
						<span class="g-pay-file-unlock-edit__doc-name">
							{{ form.fileName || '请上传文档，系统将自动解析为预览图' }}
						</span>
						<span v-if="pageCount" class="g-pay-file-unlock-edit__doc-meta">共 {{ pageCount }} 页</span>
					</div>
					<button
						type="button"
						class="g-pay-file-unlock-edit__upload-btn"
						@click="triggerDocumentSelect"
					>{{ form.fileName ? '更换文档' : '上传文档' }}</button>
				</template>
			</div>

			<div
				v-if="form.items.length && !isParsing"
				class="g-pay-file-unlock-edit__grid"
				:class="{ 'is-list-layout': isListVariant }"
			>
				<div
					v-for="(item, index) in form.items"
					:key="item.id"
					class="g-pay-file-unlock-edit__cell"
				>
					<div class="g-pay-file-unlock-edit__thumb">
						<span class="g-pay-file-unlock-edit__page-index">{{ formatPageIndex(index) }}</span>
						<img v-if="getImageUrl(item)" :src="getImageUrl(item)" alt="" />
						<div v-else class="g-pay-file-unlock-edit__thumb-empty">暂无预览</div>
					</div>
				</div>
			</div>
			<div v-else-if="!isParsing" class="g-pay-file-unlock-edit__empty">
				上传 PDF、Word、PPT 或 Excel 后，将自动解析分页预览图
			</div>
		</div>
		</div>

		<input
			ref="documentInput"
			type="file"
			:accept="documentAccept"
			class="g-pay-file-unlock-edit__file-input"
			@change="onDocumentSelect"
		/>

		<template #footer>
			<a-button @click="onCancel">取消</a-button>
			<a-button type="primary" :disabled="isParsing" @click="onConfirm">确定</a-button>
		</template>
	</a-modal>
</template>

<script>
	import UploadProgressPanel from './UploadProgressPanel.vue';
	import ColorPicker from './ColorPicker.vue';
	import { revokeLocalImageUrl } from '../utils/localImage.js';
	import { revokeLocalFileItem } from '../utils/localFile.js';
	import { normalizeHexColor } from '../utils/richText.js';
	import {
		getPayFileUnlockPreviewImageUrl,
		normalizePayFileUnlockData,
	} from '../utils/payFileUnlock.js';
	import { normalizePayFileListData } from '../utils/payFileList.js';
	import {
		filterValidPayFileDocuments,
		PAY_FILE_DOCUMENT_ACCEPT,
		uploadAndParsePayFileDocument,
	} from '../utils/uploadPayFileDocument.js';
	import { createUploadTasks, updateUploadTaskProgress } from '../utils/uploadHeaderMedia.js';

	export default {
		name: 'GPayFileUnlockEditModal',
		components: { UploadProgressPanel, ColorPicker },
		props: {
			visible: {
				type: Boolean,
				default: false,
			},
			data: {
				type: Object,
				default: () => ({}),
			},
		},
		emits: ['update:visible', 'confirm'],
		data() {
			return {
				form: normalizePayFileUnlockData({}),
				isParsing: false,
				uploadTasks: [],
				documentAccept: PAY_FILE_DOCUMENT_ACCEPT,
				processingText: '',
			};
		},
		computed: {
			pageCount() {
				return this.form.items.length;
			},
			isListVariant() {
				return Array.isArray(this.data?.headerFeatures) && this.data.headerFeatures.length > 0;
			},
		},
		watch: {
			visible(val) {
				if (val) {
					this.syncForm();
				}
			},
			data: {
				deep: true,
				handler() {
					if (this.visible) {
						this.syncForm();
					}
				},
			},
		},
		methods: {
			normalizeColor(color) {
				return normalizeHexColor(color || '#285af9');
			},
			onColorInput(field, color) {
				this.form[field] = color;
			},
			getImageUrl(item) {
				return getPayFileUnlockPreviewImageUrl(item?.image);
			},
			formatPageIndex(index) {
				return String(index + 1).padStart(2, '0');
			},
			syncForm() {
				this.form = this.isListVariant
					? normalizePayFileListData(this.data)
					: normalizePayFileUnlockData(this.data);
			},
			revokeParsedPreviewItems(items = []) {
				items.forEach((item) => {
					if (item?.image?.local) {
						revokeLocalImageUrl(item.image.previewUrl);
					}
				});
			},
			triggerDocumentSelect() {
				this.$refs.documentInput?.click();
			},
			async onDocumentSelect(event) {
				const file = filterValidPayFileDocuments(Array.from(event.target.files || []), 1)[0];
				event.target.value = '';
				if (!file) {
					this.$message?.error?.('请上传 PDF、Word、PPT 或 Excel 文档');
					return;
				}

				this.isParsing = true;
				this.uploadTasks = createUploadTasks([file]);

				try {
					const result = await uploadAndParsePayFileDocument(this, file, (payload) => {
						updateUploadTaskProgress(this.uploadTasks, {
							index: 0,
							percent: payload.percent,
							filename: payload.filename,
						});
						if (payload.status) {
							this.processingText = payload.status;
						}
					});

					this.revokeParsedPreviewItems(this.form.items);
					if (this.form.file?.local) {
						revokeLocalFileItem(this.form.file);
					}

					this.form.fileName = result.fileName;
					this.form.file = result.file;
					this.form.items = result.items;
					if (this.form.freePreviewCount > result.items.length) {
						this.form.freePreviewCount = Math.min(2, result.items.length);
					}
				} catch (error) {
					this.$message?.error?.(error?.message || '文档解析失败');
				} finally {
					this.isParsing = false;
					this.processingText = '';
					this.uploadTasks = [];
				}
			},
			onCancel() {
				this.$emit('update:visible', false);
			},
			onConfirm() {
				if (!this.form.items.length) {
					this.$message?.error?.('请先上传文档');
					return;
				}
				this.$emit('confirm', this.isListVariant
					? normalizePayFileListData(this.form)
					: normalizePayFileUnlockData(this.form));
				this.$emit('update:visible', false);
			},
		},
	};
</script>

<style scoped lang="less">
	.g-pay-file-unlock-edit-form__row {
		display: flex;
		gap: 16px;
	}

	.g-pay-file-unlock-edit-form__col {
		flex: 1;
		min-width: 0;
	}

	.g-pay-file-unlock-edit-form__colors {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.g-pay-file-unlock-edit-form__color-item {
		display: flex;
		align-items: center;
		gap: 8px;
		width: 100%;
	}

	.g-pay-file-unlock-edit-form__swatch {
		position: relative;
		width: 28px;
		height: 28px;
		border-radius: 4px;
		border: 1px solid #f0f0f0;
		overflow: hidden;
		flex-shrink: 0;
	}

	.g-pay-file-unlock-edit-form__color-input {
		position: absolute;
		inset: 0;
		opacity: 0;
		cursor: pointer;
	}

	.g-pay-file-unlock-edit {
		margin-top: 8px;
		padding-top: 12px;
	}

	.g-pay-file-unlock-edit__doc {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		min-height: 52px;
		margin-bottom: 12px;
		padding: 12px;
		border: 1px solid #f0f0f0;
		border-radius: 8px;
		background: #fafafa;
		box-sizing: border-box;
		overflow: hidden;
	}

	.g-pay-file-unlock-edit__processing {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 8px;
		width: 100%;
		min-height: 52px;
	}

	.g-pay-file-unlock-edit__processing-line {
		display: flex;
		align-items: center;
		gap: 8px;

		:deep(.ant-spin) {
			flex-shrink: 0;
		}
	}

	.g-pay-file-unlock-edit__processing-text {
		font-size: 13px;
		color: #666;
	}

	.g-pay-file-unlock-edit__doc-info {
		flex: 1;
		min-width: 0;
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.g-pay-file-unlock-edit__doc-name {
		font-size: 14px;
		color: #333;
		word-break: break-all;
		line-height: 1.4;
	}

	.g-pay-file-unlock-edit__doc-meta {
		font-size: 12px;
		color: #999;
	}

	.g-pay-file-unlock-edit__upload-btn {
		flex-shrink: 0;
		border: 1px solid #285af9;
		background: #fff;
		color: #285af9;
		border-radius: 4px;
		padding: 6px 14px;
		font-size: 13px;
		cursor: pointer;

		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}
	}

	.g-pay-file-unlock-edit__grid {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 10px;
		overflow-y: auto;
		padding-right: 4px;

		&.is-list-layout {
			display: flex;
			flex-direction: column;
			gap: 10px;
		}
	}

	.g-pay-file-unlock-edit__cell {
		border-radius: 8px;
		background: #fff;
		border: 1px solid #f0f0f0;
		box-sizing: border-box;
		overflow: hidden;
	}

	.g-pay-file-unlock-edit__thumb {
		position: relative;
		width: 100%;
		aspect-ratio: 3 / 4;
		overflow: hidden;
		background: #f5f5f5;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			display: block;
		}
	}

	.g-pay-file-unlock-edit__grid.is-list-layout .g-pay-file-unlock-edit__thumb {
		aspect-ratio: 16 / 10;
	}

	.g-pay-file-unlock-edit__page-index {
		position: absolute;
		top: 6px;
		left: 6px;
		z-index: 2;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-width: 22px;
		height: 18px;
		padding: 0 5px;
		border-radius: 4px;
		background: #285af9;
		color: #fff;
		font-size: 10px;
		font-weight: 600;
		line-height: 1;
	}

	.g-pay-file-unlock-edit__thumb-empty {
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 12px;
		color: #bbb;
	}

	.g-pay-file-unlock-edit__empty {
		padding: 28px 12px;
		text-align: center;
		font-size: 13px;
		color: #999;
		background: #fafafa;
		border-radius: 8px;
	}

	.g-pay-file-unlock-edit__file-input {
		display: none;
	}
</style>

<style lang="less">
	@import '../styles/edit-float-panel.less';

	.g-pay-file-unlock-edit-modal {
		.edit-modal-body();
		.edit-modal-scroll();
	}
</style>
